import React from 'react';
import {
  UnchangedFieldItem,
  AddedFieldItem,
  RemovedFieldItem,
  ModifiedFieldItem,
} from './components/DiffFieldComponents';

interface SideBySideSectionDiffViewProps {
  section: {
    id: string;
    title: string;
    fields: Array<{
      id: string;
      label: string;
      type: string;
      value?: any;
    }>;
  };
  changeType: 'added' | 'removed' | 'modified' | 'unchanged';
  fieldChanges?: Map<
    string,
    {
      type: 'added' | 'removed' | 'modified' | 'unchanged';
      oldField?: any;
      newField?: any;
    }
  >;
  addedFields?: Array<{
    fieldId: string;
    fieldLabel: string;
    fieldType: string;
    field?: any; // Complete field definition from blueprint
  }>;
  onFieldChange?: (sectionId: string, fieldId: string, value: any) => void;
}

export const SideBySideSectionDiffView: React.FC<SideBySideSectionDiffViewProps> = ({
  section,
  changeType,
  fieldChanges,
  addedFields = [],
  onFieldChange,
}) => {
  // Create a handler that includes the section ID
  const handleFieldChange = (fieldId: string, value: any) => {
    if (onFieldChange) {
      onFieldChange(section.id, fieldId, value);
    }
  };
  // Build a combined list of all fields (old and new)
  const allFieldIds = new Set<string>();
  section.fields.forEach(f => allFieldIds.add(f.id));
  addedFields.forEach(f => allFieldIds.add(f.fieldId));
  fieldChanges?.forEach((change, fieldId) => allFieldIds.add(fieldId));

  const fieldsList = Array.from(allFieldIds).map(fieldId => {
    const currentField = section.fields.find(f => f.id === fieldId);
    const fieldChange = fieldChanges?.get(fieldId);
    const addedField = addedFields.find(f => f.fieldId === fieldId);

    let changeType: 'added' | 'removed' | 'modified' | 'unchanged' = 'unchanged';
    let oldField = currentField;
    let newField = currentField;

    if (fieldChange) {
      changeType = fieldChange.type;
      if (changeType === 'modified') {
        // For modified fields, preserve the value from the current field
        // Old field shows old structure with current value
        oldField = { ...currentField, ...fieldChange.oldField };
        // New field shows new structure with the SAME value (to show data is preserved)
        newField = { ...currentField, ...fieldChange.newField, value: currentField?.value };
      } else if (changeType === 'removed') {
        // Field is being removed, show current value on left
        oldField = currentField;
        newField = undefined;
      } else if (changeType === 'added') {
        // Field is being added, no current value exists
        oldField = undefined;
        newField = fieldChange.newField;
      }
    } else if (addedField && !currentField) {
      changeType = 'added';
      oldField = undefined;
      // Use complete field definition if available, otherwise construct from basic info
      // New fields won't have a value yet
      newField = addedField.field || {
        id: addedField.fieldId,
        label: addedField.fieldLabel,
        type: addedField.fieldType,
      };
    }

    return {
      id: fieldId,
      changeType,
      oldField,
      newField,
    };
  });

  const getSectionBgColor = () => {
    switch (changeType) {
      case 'added':
        return 'bg-green-50 dark:bg-green-950/30';
      case 'removed':
        return 'bg-red-50 dark:bg-red-950/30';
      case 'modified':
        return 'bg-yellow-50 dark:bg-yellow-950/30';
      default:
        return 'bg-gray-50 dark:bg-gray-800';
    }
  };

  return (
    <div className={`border border-gray-300 dark:border-gray-600 rounded overflow-hidden ${getSectionBgColor()}`}>
      {/* Section Header */}
      <div className="px-4 py-3 border-b border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">section name:</span>
              <div className="heading-4-compact">{section.title}</div>
          </div>
        </div>
      </div>

      {/* Side-by-side field comparison */}
      {fieldsList.length > 0 && (
        <div className="divide-y divide-gray-300 dark:divide-gray-600">
          {fieldsList.map(({ id, changeType, oldField, newField }) => {
            if (changeType === 'unchanged' && oldField) {
              return <UnchangedFieldItem key={id} field={oldField} onFieldChange={handleFieldChange} />;
            }
            
            if (changeType === 'added' && newField) {
              return <AddedFieldItem key={id} field={newField} onFieldChange={handleFieldChange} />;
            }
            
            if (changeType === 'removed' && oldField) {
              return <RemovedFieldItem key={id} field={oldField} />;
            }
            
            if (changeType === 'modified' && oldField && newField) {
              return <ModifiedFieldItem key={id} oldField={oldField} newField={newField} onFieldChange={handleFieldChange} />;
            }
            
            return null;
          })}
        </div>
      )}

      {fieldsList.length === 0 && (
        <div className="p-4 text-sm text-gray-500 dark:text-gray-400 italic text-center">
          No fields in this section
        </div>
      )}
    </div>
  );
};

